import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { TabKey } from '@/types'

import Link from 'next/link'
import {
    BriefcaseBusiness,
    Building2,
    CircleUserRound,
    Radio,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface TabBarProps {
    active: TabKey
}

const TABS: { key: TabKey, href: string, label: string, icon: LucideIcon }[] = [
    { key: 'home', href: '/', label: 'Segnali', icon: Radio },
    { key: 'jobs', href: '/annunci', label: 'Annunci', icon: BriefcaseBusiness },
    { key: 'applications', href: '/candidature', label: 'Candidature', icon: Send },
    { key: 'companies', href: '/aziende', label: 'Aziende', icon: Building2 },
    { key: 'profile', href: '/profilo', label: 'Profilo', icon: CircleUserRound }
]

export const TabBar: FC<TabBarProps> = ({ active }): ReactNode => {
    const activeIndex = Math.max(0, TABS.findIndex(tab => tab.key === active))

    return (
        <nav
            aria-label='Navigazione principale'
            className='pointer-events-none absolute inset-x-0 bottom-0 z-40 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
        >
            <div className='pointer-events-auto relative flex h-[68px] animate-in items-stretch rounded-[28px] border border-white/70 bg-white/85 p-1.5 shadow-[0_18px_40px_-16px_rgba(26,22,19,0.45),0_2px_6px_rgba(26,22,19,0.06)] backdrop-blur-xl duration-500 fade-in slide-in-from-bottom-6'>
                <span
                    aria-hidden='true'
                    className='absolute inset-y-1.5 left-1.5 w-[calc((100%-0.75rem)/5)] rounded-[22px] bg-brand-soft transition-transform duration-500 ease-[cubic-bezier(0.65,0,0.35,1)]'
                    style={{ transform: `translateX(${activeIndex * 100}%)` }}
                />

                {TABS.map(tab => {
                    const isActive = tab.key === active
                    const Icon = tab.icon

                    return (
                        <Link
                            key={tab.key}
                            href={tab.href}
                            aria-current={isActive ? 'page' : undefined}
                            className={cn(
                                'relative z-10 flex flex-1 flex-col items-center justify-center gap-1 rounded-[22px] text-[11px] font-semibold tracking-tight transition-colors duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                                isActive ? 'text-brand-ink' : 'text-ink/70 hover:text-ink'
                            )}
                        >
                            <Icon
                                aria-hidden='true'
                                className={cn('size-[22px] transition-transform duration-300', isActive && 'scale-110')}
                                strokeWidth={isActive ? 2.3 : 1.9}
                            />

                            {tab.label}
                        </Link>
                    )
                })}
            </div>
        </nav>
    )
}
